import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Activity, CheckCircle, AlertCircle, Clock, Loader2,
  ChevronDown, ChevronUp, RefreshCw, DollarSign, Zap
} from 'lucide-react';
import apiClient from '@/services/api/apiClient';
import { Pagination } from '@/components/common/Pagination';
import { PageHeader } from '@/components/common/PageHeader';
import type { AgentRunLog } from '../services/prospectingService';

const STATUS_CONFIG: Record<string, { cls: string; icon: any }> = {
  SUCCESS: { cls: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800', icon: CheckCircle },
  PARTIAL: { cls: 'text-amber-600 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800',   icon: AlertCircle },
  FAILED:  { cls: 'text-red-600 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800',             icon: AlertCircle },
  RUNNING: { cls: 'text-blue-600 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800',        icon: Loader2 },
};

export default function AgentLogsPage() {
  const navigate = useNavigate();
  const { canRead: canViewLogs } = usePermissions('users');

  // All hooks must be before any conditional return
  const [logs, setLogs]           = useState<AgentRunLog[]>([]);
  const [loading, setLoading]     = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]         = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [totalCost, setTotalCost] = useState(0);

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/prospecting/agent-logs', { params: { page: p, limit: 20 } });
      setLogs(data.data);
      setTotalPages(data.totalPages);
      setTotal(data.total ?? (data.totalPages || 1) * 20);
      setPage(p);
      const tokens = data.data.reduce((s: number, l: AgentRunLog) => s + (l.totalTokens || 0), 0);
      const cost   = data.data.reduce((s: number, l: AgentRunLog) => s + (l.totalCostUsd || 0), 0);
      setTotalTokens(tokens);
      setTotalCost(cost);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewLogs) fetchLogs();
    else setLoading(false);
  }, [fetchLogs, canViewLogs]);

  const fmtMs = (ms: number | null) => {
    if (!ms) return '—';
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  // Access guard — rendered after all hooks
  if (!canViewLogs) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
        <Activity className="h-8 w-8 opacity-30" />
        <p className="text-sm font-medium">Access restricted</p>
        <p className="text-xs">Agent Logs are only available to Administrators and ABM Managers.</p>
        <button onClick={() => navigate('/prospecting')} className="text-xs text-primary hover:underline">
          Back to Prospecting
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <PageHeader
        title="Agent Run Logs"
        description="Pipeline observability — inputs, outputs, cost, latency"
        icon={Activity}
        onBack={() => navigate('/prospecting')}
        actions={
          <button onClick={() => fetchLogs(page)} disabled={loading}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
        }
      />

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total runs',   value: logs.length,                               icon: Activity,    cls: 'text-foreground' },
          { label: 'Successful',   value: logs.filter(l => l.status === 'SUCCESS').length, icon: CheckCircle, cls: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Total tokens', value: totalTokens.toLocaleString(),               icon: Zap,         cls: 'text-blue-600 dark:text-blue-400' },
          { label: 'Est. cost',    value: `$${totalCost.toFixed(4)}`,                 icon: DollarSign,  cls: 'text-purple-600 dark:text-purple-400' },
        ].map(({ label, value, icon: Icon, cls }) => (
          <div key={label} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
            <Icon className={`h-4 w-4 shrink-0 ${cls}`} />
            <div>
              <p className={`text-base font-bold leading-none ${cls}`}>{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Log list */}
      {loading && logs.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
          No agent runs yet. Start a prospecting run to see logs here.
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map(log => {
            const cfg = STATUS_CONFIG[log.status] || STATUS_CONFIG.RUNNING;
            const Icon = cfg.icon;
            const isExpanded = expandedId === log.runId;

            return (
              <div key={log.runId} className="rounded-xl border border-border bg-card overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : log.runId)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${cfg.cls}`}>
                    <Icon className={`h-3 w-3 ${log.status === 'RUNNING' ? 'animate-spin' : ''}`} />
                    {log.status}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{log.agentName}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(log.createdAt).toLocaleString()} · {log.triggeredBy?.name || '—'}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground shrink-0">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtMs(log.latencyMs)}</span>
                    {(log.totalTokens ?? 0) > 0 && (
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3" />{log.totalTokens!.toLocaleString()}
                      </span>
                    )}
                    {(log.totalCostUsd ?? 0) > 0 && (
                      <span className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                        <DollarSign className="h-3 w-3" />${log.totalCostUsd!.toFixed(4)}
                      </span>
                    )}
                    {log.modelsUsed?.length > 0 && (
                      <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground">
                        {log.modelsUsed[0].split('/').pop()}
                      </span>
                    )}
                  </div>
                  {isExpanded
                    ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                </button>

                {isExpanded && (
                  <div className="border-t border-border p-4 space-y-4 bg-muted/20">
                    {log.errorLog && (
                      <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                        <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">Errors</p>
                        <pre className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap overflow-auto max-h-32">
                          {JSON.stringify(log.errorLog, null, 2)}
                        </pre>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Input</p>
                        <pre className="text-xs bg-background border border-border rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap">
                          {JSON.stringify(log.inputJson, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Output</p>
                        <pre className="text-xs bg-background border border-border rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap">
                          {log.outputJson ? JSON.stringify(log.outputJson, null, 2) : '—'}
                        </pre>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span>Run ID: <code className="text-foreground">{log.runId}</code></span>
                      {log.batchId && <span>Batch: <code className="text-foreground">{log.batchId.slice(0, 8)}…</code></span>}
                      <span>Models: {(log.modelsUsed || []).join(', ') || '—'}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={20}
              totalItems={total}
              onPageChange={fetchLogs}
            />
          )}
        </div>
      )}
    </div>
  );
}
